/* Rear-hinged feeder lid with a separate, freely hanging front flap.
   Inches, lbf and degrees are input units. Positive actuator force is push;
   negative force is pull. Opening and closing include opposite resistance
   torques. The passive flap loads the main lid at its leading-edge hinge. */
(function (root) {
  'use strict';
  const R = Math.PI / 180;
  const VERSION = '1.0.0';
  const DEFAULTS = Object.freeze({
    boxHeight: 60, boxDepth: 48, boxWidth: 72,
    lidWeight: 80, flapWeight: 20, flapHeight: 18,
    closedAngle: 0, openAngle: 55,
    baseX: 8, baseY: -17, mountAlong: 22, mountOffset: 0,
    actuatorCount: 2, resistanceTorque: 30, efficiency: 90, factor: 1.5
  });
  const distance = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
  const rotate = (x, y, a) => [x * Math.cos(a) - y * Math.sin(a), x * Math.sin(a) + y * Math.cos(a)];
  const requireValue = (condition, message) => {if (!condition) throw new Error(message);};
  function prepare(input) {
    const p = {...DEFAULTS, ...input};
    for (const key of Object.keys(DEFAULTS)) requireValue(typeof p[key] === 'number' && Number.isFinite(p[key]), 'Enter a finite number for ' + key + '.');
    requireValue(p.boxHeight > 0 && p.boxDepth > 0 && p.boxWidth > 0, 'Feeder height, depth and width must be positive.');
    requireValue(p.lidWeight >= 0 && p.flapWeight >= 0 && p.resistanceTorque >= 0, 'Lid weight, flap weight and hinge resistance cannot be negative.');
    requireValue(p.flapHeight > 0 && p.flapHeight <= p.boxHeight, 'The front flap height must be positive and no greater than the feeder height.');
    requireValue(p.closedAngle >= 0 && p.openAngle <= 120 && p.openAngle > p.closedAngle,
      'Use a closed angle of at least 0° and a larger open angle no greater than 120°.');
    requireValue(p.mountAlong > 0 && p.mountAlong <= p.boxDepth && Math.abs(p.mountOffset) <= p.boxDepth,
      'Place the moving pin along the lid, with a perpendicular bracket offset no larger than the lid depth.');
    requireValue(p.baseX >= 0 && p.baseX <= p.boxDepth && p.baseY >= -p.boxHeight && p.baseY <= 0,
      'Place the fixed pin on the feeder side: X between 0 and its depth, Y between minus its height and 0.');
    requireValue(Number.isInteger(p.actuatorCount) && p.actuatorCount >= 1 && p.actuatorCount <= 4,
      'Use a whole number of actuators between 1 and 4.');
    requireValue(p.factor >= 1 && p.efficiency > 0 && p.efficiency <= 100,
      'Use a design allowance of at least 1 and efficiency greater than 0 and no more than 100%.');
    const lo = p.closedAngle * R, hi = p.openAngle * R;
    const pinRadius = Math.hypot(p.mountAlong, p.mountOffset);
    const baseRadius = Math.hypot(p.baseX, p.baseY);
    requireValue(baseRadius > 1e-8, 'The fixed actuator pin cannot be at the lid hinge.');
    // Exact collinearity events are checked between samples, not merely at frames.
    const rootAngle = Math.atan2(p.baseY, p.baseX) - Math.atan2(p.mountOffset, p.mountAlong);
    const first = Math.ceil((lo - rootAngle - 1e-10) / Math.PI);
    const last = Math.floor((hi - rootAngle + 1e-10) / Math.PI);
    requireValue(first > last, 'The actuator crosses a dead center in this angle range. Move its mounting pins or reduce the lid angle range.');
    const prepared = {p, lo, hi, pinRadius, allowance: p.factor / (p.efficiency / 100) / p.actuatorCount,
      gravityScale: p.lidWeight * p.boxDepth / 2 + p.flapWeight * p.boxDepth};
    for (const angle of [lo, hi]) {
      const pin = rotate(p.mountAlong, p.mountOffset, angle);
      const length = distance(pin, [p.baseX, p.baseY]);
      requireValue(length > .01, 'The actuator pins coincide. Move a mounting pin.');
      const arm = (p.baseX * pin[1] - p.baseY * pin[0]) / length;
      requireValue(Math.abs(arm) / pinRadius >= Math.sin(2 * R),
        'The actuator approaches a dead center within 2° of the lid-pin radius. Move its mounting pins for usable leverage.');
    }
    return prepared;
  }
  function at(prepared, t, options = {}) {
    requireValue(typeof t === 'number' && Number.isFinite(t) && t >= 0 && t <= 1, 'Animation position must be between 0 and 1.');
    const swing = options.flapSwingAngle ?? 0;
    requireValue(typeof swing === 'number' && Number.isFinite(swing) && Math.abs(swing) <= 90, 'Flap demonstration angle must be between −90° and 90°.');
    const {p, lo, hi, allowance, gravityScale} = prepared;
    const theta = lo + (hi - lo) * t;
    const hinge = [0, p.boxHeight];
    const localPin = rotate(p.mountAlong, p.mountOffset, theta);
    const pin = [localPin[0], p.boxHeight + localPin[1]];
    const base = [p.baseX, p.boxHeight + p.baseY];
    const length = distance(base, pin);
    const momentArm = (p.baseX * localPin[1] - p.baseY * localPin[0]) / length;
    const lidTip = [p.boxDepth * Math.cos(theta), p.boxHeight + p.boxDepth * Math.sin(theta)];
    const lidCG = [lidTip[0] / 2, p.boxHeight + (lidTip[1] - p.boxHeight) / 2];
    const flapVector = [p.flapHeight * Math.sin(swing * R), -p.flapHeight * Math.cos(swing * R)];
    const flapTip = [lidTip[0] + flapVector[0], lidTip[1] + flapVector[1]];
    const flapCG = [lidTip[0] + flapVector[0] / 2, lidTip[1] + flapVector[1] / 2];
    const gravityTorque = gravityScale * Math.cos(theta);
    const signedOpenForce = (gravityTorque + p.resistanceTorque) / momentArm * allowance;
    const signedCloseForce = (gravityTorque - p.resistanceTorque) / momentArm * allowance;
    const pushDemand = Math.max(0, signedOpenForce, signedCloseForce);
    const pullDemand = Math.max(0, -signedOpenForce, -signedCloseForce);
    return {t, angle: theta / R, theta, hinge, base, pin, localPin, lidTip, lidCG,
      flapHinge: lidTip.slice(), flapTip, flapCG, flapSwingAngle: swing,
      flapSwingSizingIgnored: swing !== 0,
      length, momentArm, gravityTorque, signedOpenForce, signedCloseForce,
      pushDemand, pullDemand, force: Math.max(pushDemand, pullDemand),
      rawOpenForce: (gravityTorque + p.resistanceTorque) / momentArm / p.actuatorCount,
      rawCloseForce: (gravityTorque - p.resistanceTorque) / momentArm / p.actuatorCount};
  }
  function geometry(parameters, t, options) {return at(prepare(parameters), t, options);}
  function refine(prepared, samples, key) {
    let best = samples.reduce((a, b) => b[key] > a[key] ? b : a);
    for (let i = 1; i < samples.length - 1; i++) {
      if (samples[i][key] < samples[i - 1][key] || samples[i][key] < samples[i + 1][key]) continue;
      if (samples[i][key] === samples[i - 1][key] && samples[i][key] === samples[i + 1][key]) continue;
      let a = samples[i - 1].t, b = samples[i + 1].t;
      for (let k = 0; k < 32; k++) {
        const u = a + (b - a) / 3, v = b - (b - a) / 3;
        if (at(prepared, u)[key] < at(prepared, v)[key]) a = u; else b = v;
      }
      const point = at(prepared, (a + b) / 2);
      if (point[key] > best[key]) best = point;
    }
    return best;
  }
  function solved(prepared) {
    const samples = Array.from({length: 241}, (_, i) => at(prepared, i / 240));
    const closed = samples[0], open = samples[samples.length - 1];
    // No dead center in the complete sweep means pin length is monotonic.
    const minLength = Math.min(closed.length, open.length);
    const maxLength = Math.max(closed.length, open.length);
    const push = refine(prepared, samples, 'pushDemand');
    const pull = refine(prepared, samples, 'pullDemand');
    const peakSample = push.pushDemand >= pull.pullDemand ? push : pull;
    return {version: VERSION, parameters: {...prepared.p}, samples, closed, open,
      minLength, maxLength, stroke: maxLength - minLength,
      peak: Math.max(push.pushDemand, pull.pullDemand), peakAt: peakSample.t,
      maxPush: push.pushDemand, maxPull: pull.pullDemand,
      pushPeakAt: push.t, pullPeakAt: pull.t,
      openingExtends: open.length > closed.length,
      actuatorCount: prepared.p.actuatorCount,
      totalMovingWeight: prepared.p.lidWeight + prepared.p.flapWeight};
  }
  function solve(parameters) {return solved(prepare(parameters));}
  function fitModel(parameters, closedLength, usableStroke, options = {}) {
    // A finite search proposes mounting points only; it cannot establish wall or
    // bracket strength. The caller must explicitly apply any returned changes.
    const original = prepare(parameters), p = original.p;
    requireValue(Number.isFinite(closedLength) && closedLength > 0 && Number.isFinite(usableStroke) && usableStroke > 0,
      'Actuator fitting needs a positive retracted length and usable stroke.');
    const forceLimit = options.forceLimit ?? Infinity;
    const pushLimit = options.pushLimit ?? forceLimit;
    const pullLimit = options.pullLimit ?? forceLimit;
    for (const limit of [forceLimit, pushLimit, pullLimit]) requireValue((Number.isFinite(limit) || limit === Infinity) && limit >= 0, 'Force limits must be nonnegative.');
    const maxLength = closedLength + usableStroke;
    let best = null, checked = 0;
    const visited = new Set();
    function tryBase(x, y) {
      if (x < -1e-8 || x > p.boxDepth + 1e-8 || y < -p.boxHeight - 1e-8 || y > 1e-8) return;
      x = Math.min(p.boxDepth, Math.max(0, x));
      y = Math.min(0, Math.max(-p.boxHeight, y));
      const key = x.toFixed(6) + ':' + y.toFixed(6);
      if (visited.has(key)) return;
      visited.add(key); checked++;
      const distanceMoved = Math.hypot(x - p.baseX, y - p.baseY);
      if (best && distanceMoved >= best.distance - 1e-9) return;
      try {
        const candidate = prepare({...p, baseX: x, baseY: y});
        const start = at(candidate, 0), end = at(candidate, 1);
        const min = Math.min(start.length, end.length), max = Math.max(start.length, end.length);
        if (min < closedLength - 1e-7 || max > maxLength + 1e-7) return;
        const result = solved(candidate);
        if (result.peak > forceLimit + 1e-7 || result.maxPush > pushLimit + 1e-7 || result.maxPull > pullLimit + 1e-7) return;
        best = {parameters: {...candidate.p}, baseX: x, baseY: y, result,
          distance: distanceMoved, moved: distanceMoved > 1e-7};
      } catch (_) { /* Rejected geometry is not a mounting proposal. */ }
    }
    tryBase(p.baseX, p.baseY);
    if (best) return {...best, checked};
    for (let i = 0; i <= 30; i++) for (let j = 0; j <= 30; j++) tryBase(p.boxDepth * i / 30, -p.boxHeight * j / 30);
    // Endpoint pin circles capture narrow feasible regions missed by the grid.
    for (const angle of [original.lo, original.hi]) {
      const pin = rotate(p.mountAlong, p.mountOffset, angle);
      for (const radius of [closedLength, closedLength + usableStroke / 2, maxLength]) {
        for (let k = 0; k < 120; k++) {
          const a = k * Math.PI / 60;
          tryBase(pin[0] + radius * Math.cos(a), pin[1] + radius * Math.sin(a));
        }
      }
    }
    if (!best) return null;
    // Refine toward the original point without moving any other dimension.
    let stepX = p.boxDepth / 30, stepY = p.boxHeight / 30;
    for (let pass = 0; pass < 8; pass++) {
      const b = best;
      for (const dx of [-1, 0, 1]) for (const dy of [-1, 0, 1]) tryBase(b.baseX + dx * stepX, b.baseY + dy * stepY);
      stepX /= 2; stepY /= 2;
    }
    return {...best, checked};
  }
  const api = {version: VERSION, defaults: DEFAULTS, geometry, solve, fitModel};
  root.FGCattleFeederModel = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
